import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from "react";

import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

import {
    COVERS_HALFCARD_CONSTANTS,
    COVERS_MIN,
    COVERS_MAX,
    COVERS_MAX_DIGITS,
    OBJECT_NAME,
    OBJECT_VALUES,
} from "./constant";
import {
    Container,
    IllustrationContainer,
    IconWrapper,
    TextContainer,
    InputWrapper,
    CTAWrapper,
} from "./styled";
import { ICoversHalfCardProps } from "./types";
import { useAnalytics } from "../hooks";

// While the Android keyboard is open, useKeyboardState (client) lifts the page by setting a
// transform on <body> plus overflow/touch locks on <html>. Those are cleared only when native
// fires keyboardStateChanged(false) — async, after the close animation. When this card hands off
// to another fixed half-card (e.g. PIN verification) that mount can win the race, and a lingering
// <body> transform makes <body> the containing block for the new position:fixed card, pinning it
// to the top. Clearing the same properties the hook resets, synchronously on close, prevents that.
const releaseKeyboardLift = (): void => {
    if (typeof document === "undefined") return;
    (document.activeElement as HTMLElement | null)?.blur();
    document.body.style.transform = "translateY(0)";
    document.documentElement.style.removeProperty("overflow-y");
    document.documentElement.style.removeProperty("touch-action");
    // The keyboard lift now lives in --ios-keyboard-height (see styled.ts). Clear it synchronously
    // on submit/close so the PIN half-card that mounts next does not inherit a stale lift before
    // native's async keyboardStateChanged(false) fires.
    document.documentElement.style.removeProperty("--ios-keyboard-height");
};

const CoversHalfCard = ({
    show,
    onClose,
    onSubmit,
    screenName,
    impressionAnalytics: defaultImpressionAnalytics,
    clickAnalytics: defaultClickAnalytics,
}: ICoversHalfCardProps) => {
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });
    const [coversInput, setCoversInput] = useState("");
    const hasImpressionFiredRef = useRef(false);

    const handleChange = useCallback((val: string) => {
        // strip non-digits and clamp to the max number of digits. The DOM-level
        // `maxLength` below is the primary guard; this stays as a defense
        // in depth in case the DOM-level cap is bypassed (paste, autofill etc.).
        setCoversInput(val.replace(/\D/g, "").slice(0, COVERS_MAX_DIGITS));
    }, []);

    // Hard-block non-digit input at the DOM level. `inputMode="numeric"` and
    // `pattern="[0-9]*"` only constrain mobile keypads / form validation, so
    // desktop browsers (e.g. on staging) can still type alphabets. This
    // handler fires before the character is inserted and lets us reject it.
    const handleBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const data = (e.nativeEvent as InputEvent).data;
        if (data && /\D/.test(data)) {
            e.preventDefault();
        }
    }, []);

    // Fire the entry impression once each time the half-card opens. This
    // component stays mounted (HalfCard renders null while closed), so key the
    // impression on `show` rather than a mount-time observer. The ref guards
    // against duplicate fires when the enriched `impressionAnalytics` identity
    // changes (state/table-context updates) while the card is open.
    useEffect(() => {
        if (!show) {
            hasImpressionFiredRef.current = false;
            return;
        }
        if (hasImpressionFiredRef.current) return;
        hasImpressionFiredRef.current = true;
        impressionAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.IMPRESSION_ENTRY,
            value: String(1),
        });
    }, [show, impressionAnalytics]);

    const handleSubmit = useCallback(() => {
        clickAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.CLICK_SUBMIT,
            value: String(1),
            context: JSON.stringify({ guest_count: coversInput }),
        });
        // Release the Android keyboard lift before the PIN half-card mounts (see helper).
        releaseKeyboardLift();
        onSubmit(Number(coversInput));
        setCoversInput("");
    }, [clickAnalytics, coversInput, onSubmit]);

    const handleClose = useCallback(() => {
        clickAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.CLICK_CLOSE,
            value: String(1),
        });
        // Same reason as submit: don't leave the keyboard lift applied for whatever renders next.
        releaseKeyboardLift();
        setCoversInput("");
        onClose();
    }, [clickAnalytics, onClose]);

    const coversValue = Number(coversInput);
    const isCtaDisabled = !coversInput || coversValue < COVERS_MIN || coversValue > COVERS_MAX;

    const textFieldDetails = useMemo(
        () => ({
            type: TextFieldTypes.TEXT,
            name: "covers",
            label: "",
            value: coversInput,
            onChange: handleChange,
            placeHolderStyle: { display: "none" },
            attrs: {
                inputMode: "numeric" as const,
                pattern: "[0-9]*",
                maxLength: COVERS_MAX_DIGITS,
                placeholder: COVERS_HALFCARD_CONSTANTS.INPUT_LABEL,
                onBeforeInput: handleBeforeInput,
            },
        }),
        [coversInput, handleChange, handleBeforeInput]
    );

    return (
        <HalfCard
            isOpen={show}
            onClose={handleClose}
            closeOnOverlayClick={true}
            hideCloseIcon={true}
            backgroundColor="background_Secondary"
        >
            <Container>
                <IllustrationContainer>
                    <IconWrapper>
                        <Image
                            cloudinaryId={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_ID}
                            alt={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_ALT}
                            cloudinaryHeight={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE * 2}
                            cloudinaryWidth={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE * 2}
                            height={String(COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE)}
                            width={String(COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE)}
                        />
                    </IconWrapper>
                </IllustrationContainer>

                <TextContainer>
                    <Text variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                        {COVERS_HALFCARD_CONSTANTS.TITLE}
                    </Text>
                    <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                        {COVERS_HALFCARD_CONSTANTS.DESCRIPTION}
                    </Text>
                </TextContainer>

                <InputWrapper>
                    <TextField textFieldDetails={textFieldDetails} />
                </InputWrapper>

                <CTAWrapper>
                    <SuperButton
                        buttonType="primary"
                        buttonSize="large"
                        onClick={handleSubmit}
                        isDisabled={isCtaDisabled}
                    >
                        {COVERS_HALFCARD_CONSTANTS.CTA_TEXT}
                    </SuperButton>
                </CTAWrapper>
            </Container>
        </HalfCard>
    );
};

export default memo(CoversHalfCard);
